import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ExternalLink, Megaphone, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "dismissed_announcements_v1";
// Refresh public announcements every 60s so admin-created posts appear
// on already-open tabs without a manual reload.
const POLL_MS = 60_000;

/** Variant → gradient + accent icon color. */
const VARIANT_STYLES = {
    primary: {
        bg: "linear-gradient(90deg, rgba(250,204,21,0.18), rgba(250,204,21,0.05) 60%, rgba(250,204,21,0.14))",
        border: "rgba(250,204,21,0.32)",
        accent: "#facc15",
    },
    info: {
        bg: "linear-gradient(90deg, rgba(56,189,248,0.18), rgba(56,189,248,0.05) 60%, rgba(56,189,248,0.14))",
        border: "rgba(56,189,248,0.32)",
        accent: "#38bdf8",
    },
    success: {
        bg: "linear-gradient(90deg, rgba(34,197,94,0.18), rgba(34,197,94,0.05) 60%, rgba(34,197,94,0.14))",
        border: "rgba(34,197,94,0.32)",
        accent: "#22c55e",
    },
    warning: {
        bg: "linear-gradient(90deg, rgba(251,146,60,0.18), rgba(251,146,60,0.05) 60%, rgba(251,146,60,0.14))",
        border: "rgba(251,146,60,0.36)",
        accent: "#fb923c",
    },
    danger: {
        bg: "linear-gradient(90deg, rgba(239,68,68,0.20), rgba(239,68,68,0.06) 60%, rgba(239,68,68,0.16))",
        border: "rgba(239,68,68,0.36)",
        accent: "#ef4444",
    },
};

// Dismissed announcements are kept in an in-memory state only - closing the
// banner hides it for the current page view, but a refresh brings it back so
// important messages don't stay hidden forever. If we ever want persistent
// dismissals, swap the two helpers below for localStorage/sessionStorage
// implementations.
function readDismissed() {
    // Also proactively drop any leftover key from previous versions so users
    // upgrading from the persistent behaviour don't stay silently dismissed.
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
    return [];
}
function writeDismissed(_ids) {
    /* no-op - dismissal is ephemeral now (resets on refresh) */
}

/**
 * Compute time remaining until an ISO datetime string.
 * Returns null if the input is empty/invalid, or the target is in the past.
 */
function computeRemaining(iso) {
    if (!iso) return null;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return null;
    const now = Date.now();
    const ms = target - now;
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    return { days, hours, minutes, seconds, totalSec };
}

const pad2 = (n) => String(n).padStart(2, "0");

/** Live-updating countdown pill. Ticks once a second. */
function CountdownDisplay({ target, label, accent }) {
    const [remaining, setRemaining] = useState(() => computeRemaining(target));

    useEffect(() => {
        setRemaining(computeRemaining(target));
        if (!target) return undefined;
        const t = setInterval(() => {
            setRemaining(computeRemaining(target));
        }, 1000);
        return () => clearInterval(t);
    }, [target]);

    if (!remaining) return null;

    const { days, hours, minutes, seconds } = remaining;
    return (
        <span
            data-testid="announcement-countdown"
            className="inline-flex items-center gap-1 sm:gap-1.5 shrink-0 text-[10px] sm:text-xs font-mono tabular-nums text-white"
            style={{
                background: `${accent}1f`,
                border: `1px solid ${accent}55`,
                color: "white",
                padding: "2px 6px",
                borderRadius: "9999px",
            }}
        >
            <Clock size={10} strokeWidth={2.2} className="shrink-0" style={{ color: accent }} />
            {label && (
                <span
                    className="hidden md:inline text-[10px] uppercase tracking-[0.15em] opacity-80"
                    style={{ color: accent }}
                >
                    {label}
                </span>
            )}
            <span className="tabular-nums leading-none whitespace-nowrap" aria-live="polite">
                {days > 0 && (
                    <>
                        <span className="font-semibold">{days}</span>
                        <span className="opacity-70">d </span>
                    </>
                )}
                {pad2(hours)}:{pad2(minutes)}
                {/* Drop the seconds on very small screens to save ~24 px */}
                <span className="hidden xs:inline sm:inline">:{pad2(seconds)}</span>
            </span>
        </span>
    );
}

export default function AnnouncementBanner() {
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(() => readDismissed());
    const [index, setIndex] = useState(0);
    const mountedRef = useRef(true);

    // Hide the banner on admin routes and on the server-error preview page.
    const path = location.pathname || "/";
    const hidden = path.startsWith("/admin") || path === "/500";

    useEffect(() => {
        mountedRef.current = true;
        const load = async () => {
            try {
                const { data } = await api.get("/announcements");
                if (mountedRef.current) {
                    setItems(Array.isArray(data) ? data : []);
                }
            } catch {
                /* silently ignore - the banner is non-critical */
            }
        };
        load();
        const t = setInterval(load, POLL_MS);
        return () => {
            mountedRef.current = false;
            clearInterval(t);
        };
    }, []);

    // Prune dismissed IDs that no longer exist server-side so localStorage
    // doesn't grow forever.
    useEffect(() => {
        if (!items.length || !dismissed.length) return;
        const activeIds = new Set(items.map((i) => i.id));
        const pruned = dismissed.filter((id) => activeIds.has(id));
        if (pruned.length !== dismissed.length) {
            setDismissed(pruned);
            writeDismissed(pruned);
        }
    }, [items, dismissed]);

    const visible = useMemo(() => {
        const ds = new Set(dismissed);
        return items.filter((i) => !ds.has(i.id));
    }, [items, dismissed]);

    // Reset index when visible list changes so we never point past the end.
    useEffect(() => {
        if (index >= visible.length && visible.length > 0) setIndex(0);
    }, [visible, index]);

    const bannerRef = useRef(null);
    // Publish current banner height as CSS custom property `--announcement-h`
    // so the Header can slide down and content can offset if needed.
    useEffect(() => {
        const root = document.documentElement;
        const noBanner = hidden || visible.length === 0;
        if (noBanner) {
            root.style.setProperty("--announcement-h", "0px");
            return;
        }
        const el = bannerRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            root.style.setProperty("--announcement-h", "44px");
            return;
        }
        const update = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            root.style.setProperty("--announcement-h", `${h}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            ro.disconnect();
            root.style.setProperty("--announcement-h", "0px");
        };
    }, [hidden, visible.length]);

    if (visible.length === 0) return null;
    if (hidden) return null;
    const current = visible[Math.min(index, visible.length - 1)];
    const style = VARIANT_STYLES[current.variant] || VARIANT_STYLES.primary;
    const hasLink = !!(current.link_url && current.link_url.trim());
    const href = hasLink
        ? /^https?:\/\//.test(current.link_url) || current.link_url.startsWith("/")
            ? current.link_url
            : `https://${current.link_url}`
        : "";

    const dismiss = (id) => {
        const next = [...new Set([...dismissed, id])];
        setDismissed(next);
        writeDismissed(next);
    };
    const showPrev = () => setIndex((i) => (i - 1 + visible.length) % visible.length);
    const showNext = () => setIndex((i) => (i + 1) % visible.length);

    return (
        <div
            data-testid="announcement-banner-wrap"
            className="fixed top-0 left-0 right-0 z-[60] pointer-events-none overflow-hidden"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={current.id}
                    ref={bannerRef}
                    data-testid="announcement-banner"
                    data-announcement-id={current.id}
                    initial={{ y: -60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -60, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 26 }}
                    className="pointer-events-auto"
                    style={{
                        background: style.bg,
                        borderBottom: `1px solid ${style.border}`,
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                    }}
                >
                    <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-2 flex items-center gap-2 sm:gap-3 min-w-0">
                        <span
                            className="hidden sm:inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                            style={{ background: `${style.accent}22`, color: style.accent }}
                            aria-hidden="true"
                        >
                            <Megaphone size={14} strokeWidth={2} />
                        </span>

                        <div className="flex-1 min-w-0 text-xs sm:text-sm text-white leading-snug">
                            <span className="truncate block">{current.text}</span>
                            {hasLink && (
                                <a
                                    href={href}
                                    data-testid="announcement-cta"
                                    target={href.startsWith("http") ? "_blank" : "_self"}
                                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                                    className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold uppercase tracking-wider hover:underline mt-0.5 sm:mt-0 sm:ml-2"
                                    style={{ color: style.accent }}
                                >
                                    {current.link_label || "Więcej"}
                                    <ExternalLink size={10} strokeWidth={2.2} />
                                </a>
                            )}
                        </div>

                        {current.countdown_to && (
                            <CountdownDisplay
                                target={current.countdown_to}
                                label={current.countdown_label}
                                accent={style.accent}
                            />
                        )}

                        {visible.length > 1 && (
                            <div className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-white/70 shrink-0">
                                <button
                                    onClick={showPrev}
                                    data-testid="announcement-prev"
                                    aria-label="Poprzednie ogłoszenie"
                                    className="p-1 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <span className="tabular-nums">
                                    {Math.min(index, visible.length - 1) + 1}/{visible.length}
                                </span>
                                <button
                                    onClick={showNext}
                                    data-testid="announcement-next"
                                    aria-label="Następne ogłoszenie"
                                    className="p-1 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        )}

                        <button
                            data-testid="announcement-close"
                            onClick={() => dismiss(current.id)}
                            aria-label="Zamknij ogłoszenie"
                            className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                        >
                            <X size={16} strokeWidth={2} />
                        </button>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
